import React,{useEffect,useState,useRef,useCallback,useMemo} from 'react';
import { Form, Input, Button, Select, Row, Col, DatePicker, Upload, InputNumber, Checkbox, Modal, message  } from 'antd';
import ModalActivityWrap, { ConfirmModalStyles } from './ModalActivity.styles';
import IntlMessages from '@iso/components/utility/intlMessages';
import { activityPolicy } from '@iso/constants/activityPolicy';
import {
  safetyActivityHazardCategories,
  getSafetyActivityHazardsForCategory,
  getSafetyActivityHazardCategoryByHazard,
  isBiosecurityHazardCategory,
} from '@iso/constants/hazardTypes';
import { BIOSECURITY_TAXONOMY } from '@iso/constants/biosecurityTaxonomy';
import { CloseOutlined, UploadOutlined, EnvironmentOutlined, EditOutlined } from '@ant-design/icons';
import {propertyService, healthsafety, attachment, hazardService } from '@iso/services';
import _ from 'lodash';
import L from 'leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { dateHelper } from '@iso/lib/helpers/dateHelper';
import moment from 'moment';
import { Map, TileLayer, FeatureGroup, Polygon, Circle, Tooltip, ZoomControl,Marker,Popup } from 'react-leaflet';
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import TagsInput from 'react-tagsinput'
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import 'react-tagsinput/react-tagsinput.css'
import appActions from '@iso/redux/app/actions';
import notificationActions from '@iso/redux/notification/actions';
import FileUploader from "@iso/components/FileUploader/FileUploader";
import { propertyHelper } from "@iso/lib/helpers/propertyHelper";
import { useHistory } from 'react-router-dom';
import { getHazardCentroidArray } from '../../../constants/hazardGeometry';

const { changeCurrent} = appActions;
const { notificationData } = notificationActions;
const DEFAULT_LAT = -25.3444;
const DEFAULT_LNG = 131.0369;

const { Option } = Select;

const parseMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    return JSON.parse(metadata);
  } catch (error) {
    return {};
  }
};

const buildAttachmentUrl = (attachmentData) => {
  if (!attachmentData) return '';
  if (attachmentData.full_path) {
    return attachmentData.full_path;
  }
  if (attachmentData.url) {
    if (/^https?:\/\//i.test(attachmentData.url)) {
      return attachmentData.url;
    }
    const base = process.env.REACT_APP_API_BASE_URL || '';
    const separator = attachmentData.url.startsWith('/') ? '' : '/';
    return `${base}${separator}${attachmentData.url}`;
  }
  return '';
};

const getAttachmentName = (attachmentData) => {
  if (!attachmentData) return 'attachment';
  const source = attachmentData.original_filename || attachmentData.name || attachmentData.url || attachmentData.full_path || '';
  if (!source) {
    return attachmentData.id ? `attachment-${attachmentData.id}` : 'attachment';
  }
  const segments = source.split('/');
  return segments.pop() || source;
};

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Normalise polygon/rectangle vertices into the "lat, lng" string format the
// backend (Hazard::beforeValidate) expects. Inputs vary by source:
//   - freshly drawn shapes (confirmHazardArea)  -> ["lat, lng", ...]
//   - a saved hazard read back from the API     -> [["lat","lng"], ...]
// Accepting both keeps create AND edit working through the same path.
const normalizeDataArea = (area) => {
  if (!Array.isArray(area) || area.length === 0) return null;
  const points = area
    .map((coord) => {
      let lat;
      let lng;
      if (typeof coord === 'string') {
        const parts = coord.split(',');
        if (parts.length < 2) return null;
        lat = Number(parts[0].trim());
        lng = Number(parts[1].trim());
      } else if (Array.isArray(coord) && coord.length >= 2) {
        lat = Number(coord[0]);
        lng = Number(coord[1]);
      } else {
        return null;
      }
      return Number.isNaN(lat) || Number.isNaN(lng) ? null : `${lat}, ${lng}`;
    })
    .filter(Boolean);
  // A valid ring needs at least 3 points.
  return points.length >= 3 ? points : null;
};

const normalizeAttachments = (attachments = []) =>
  attachments
    .filter(Boolean)
    .map((item) => ({
      uid: item.id?.toString() || `${item.uid || item.url || Math.random()}`,
      name: getAttachmentName(item),
      status: 'done',
      url: buildAttachmentUrl(item),
      response: { attachment: item },
    }));

const extractAttachmentSources = (policy) => {
  if (!policy) return [];
  const metadata = policy.metadata || {};
  if (Array.isArray(metadata.attachments)) {
    return metadata.attachments;
  }
  if (Array.isArray(metadata.attachment)) {
    return metadata.attachment;
  }
  if (Array.isArray(metadata.attachment_activity)) {
    return metadata.attachment_activity;
  }
  if (Array.isArray(policy.attachments)) {
    return policy.attachments;
  }
  if (Array.isArray(policy.attachment)) {
    return policy.attachment;
  }
  return [];
};

const extractAttachmentIds = (policy) => {
  if (!policy) return [];
  const toNumericId = (value) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const metadataIds = policy.metadata?.attachment_activity_id;
  if (Array.isArray(metadataIds)) {
    return metadataIds
      .map(toNumericId)
      .filter((value) => value !== null);
  }
  const metadataAttachment = policy.metadata?.attachment_activity;
  if (Array.isArray(metadataAttachment)) {
    return metadataAttachment
      .map(toNumericId)
      .filter((value) => value !== null);
  }
  const sources = extractAttachmentSources(policy);
  if (sources.length) {
    return sources
      .map((item) => toNumericId(item.id))
      .filter((value) => value !== null);
  }
  return [];
};

const ModalActivity = (props) => {
  const { editingPolicy, propertyCenter, initialPoint, saveToHazardsTable = false } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const mapRef = useRef();
  const [valueLat, setValueLat] = useState();
  const [valueLng, setValueLng] = useState();
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const [primaryObjects, setPrimaryObjects] = useState([]);
  const currentUser = useSelector((state) => state.user.user);

  const [attachmentActivityId, setAttachmentActivityId] = useState([]);
  const [attachmentFileList, setAttachmentFileList] = useState([]);
  const [attendees, setAttendees] = useState([]);
  // When the user clicks "Place Hazard" while editing, the parent clears
  // editingPolicy before re-opening the modal. We stash the original id in
  // localStorage during handleDrawHazardArea and restore it here so the save
  // still issues a PUT instead of creating a duplicate row.
  const [restoredEditingPolicyId, setRestoredEditingPolicyId] = useState(null);
  const [showHazardOther, setShowHazardOther] = useState(false);
  const [position,setPosition] = useState([null, null]);
  const hazardCategoryValue = Form.useWatch(['asset', 'hazard_category'], form);
  const hazardTypeValue = Form.useWatch(['asset', 'hazard_type'], form);
  const dateTimeValue = Form.useWatch(['asset', 'date_time'], form);
  const hazardOptions = useMemo(
    () => getSafetyActivityHazardsForCategory(hazardCategoryValue),
    [hazardCategoryValue]
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const {activityActive,propertyId} = props;

  // Resolve the hazard's geometry from the best available source.
  // A freshly-drawn shape arrives via `initialPoint`; when EDITING an existing
  // hazard the modal only receives `editingPolicy`, so we must reconstruct the
  // geometry from the saved record. Without this fallback, opening the edit
  // form for a polygon/circle hazard and saving silently rewrote it to a
  // marker (geometryData defaulted to shape_type:'marker').
  const effectiveGeometry = useMemo(() => {
    const fromInitial =
      initialPoint && initialPoint.shape_type && initialPoint.shape_type !== 'marker'
        ? initialPoint
        : null;
    const src = fromInitial || editingPolicy || null;
    if (!src || !src.shape_type || src.shape_type === 'marker') return null;

    if (src.shape_type === 'polygon' || src.shape_type === 'rectangle') {
      const data_area = normalizeDataArea(src.data_area || src.area);
      return data_area ? { shape_type: src.shape_type, data_area } : null;
    }

    if (src.shape_type === 'circle') {
      const center =
        src.circle_center ||
        (src.circle_lat != null && src.circle_lng != null
          ? { lat: Number(src.circle_lat), lng: Number(src.circle_lng) }
          : null);
      const radius = src.circle_radius != null ? Number(src.circle_radius) : null;
      if (!center || radius == null || Number.isNaN(radius)) return null;
      return { shape_type: 'circle', circle_center: center, circle_radius: radius };
    }

    return null;
  }, [initialPoint, editingPolicy]);

  // Detect if hazard area geometry exists (polygon/rectangle/circle)
  const hasHazardArea = !!effectiveGeometry;

  // Human-readable area description
  const hazardAreaDescription = useMemo(() => {
    if (!effectiveGeometry) return '';
    if (effectiveGeometry.shape_type === 'polygon') {
      return `Polygon (${effectiveGeometry.data_area.length} vertices)`;
    }
    if (effectiveGeometry.shape_type === 'rectangle') {
      return 'Rectangle';
    }
    if (effectiveGeometry.shape_type === 'circle') {
      return `Circle (${Math.round(effectiveGeometry.circle_radius)}m radius)`;
    }
    return 'Area';
  }, [effectiveGeometry]);
  const activePropertySubscription = useSelector((state) => state.subscription.activePropertySubscription);
  const editingMetadata = useMemo(() => parseMetadata(editingPolicy?.metadata), [editingPolicy]);
  const activityTypeValue = Form.useWatch(['asset', 'type'], form);
  const shouldHideActivityPolicy = useMemo(() => {
    const resolvedType = activityTypeValue || activityActive || '';
    return resolvedType === 'hazard' || resolvedType === 'safety_walk';
  }, [activityTypeValue, activityActive]);
  const capturedByValue = useMemo(() => {
    return (
      editingPolicy?.created_by_name ||
      editingMetadata?.user_name ||
      editingMetadata?.user ||
      editingMetadata?.created_by ||
      editingPolicy?.user_name ||
      editingPolicy?.created_by ||
      currentUser?.name ||
      ''
    );
  }, [editingMetadata, editingPolicy, currentUser]);
  const capturedTimestamp = useMemo(() => {
    const source =
      editingMetadata?.created_at ||
      editingPolicy?.created_at ||
      editingMetadata?.date_time ||
      editingPolicy?.date_time ||
      (dateTimeValue ? dateTimeValue : null);
    if (!source) {
      return '';
    }
    const momentValue = moment(source);
    if (!momentValue.isValid()) {
      return '';
    }
    return momentValue.format('DD-MM-YYYY HH:mm:ss');
  }, [editingMetadata, editingPolicy, dateTimeValue]);
  const [userOptions, setUserOptions] = useState([]);
  const userOptionsForSelect = useMemo(() => userOptions.map((user) => {
    const displayName = user?.name || user?.email;
    const value = user?.name && user?.email && user.name !== user.email
      ? `${user.name} (${user.email})`
      : displayName;
    return { value, label: value };
  }), [userOptions]);
  const [mapInstance, setMapInstance] = useState(null);
  const hazardStatusValue = Form.useWatch(['asset', 'hazard_status'], form);
  const addAttachmentId = useCallback((id) => {
    if (id === null || id === undefined) {
      return;
    }
    const numericId = Number(id);
    const finalId = Number.isNaN(numericId) ? id : numericId;
    setAttachmentActivityId((prev) => {
      if (prev.includes(finalId)) {
        return prev;
      }
      return [...prev, finalId];
    });
  }, []);

  const removeAttachmentId = useCallback((id) => {
    if (id === null || id === undefined) {
      return;
    }
    const numericId = Number(id);
    const finalId = Number.isNaN(numericId) ? id : numericId;
    setAttachmentActivityId((prev) => prev.filter((existingId) => existingId !== finalId));
  }, []);

useEffect(() => {
  if (props.visibleModalActivity && mapInstance) {
    setTimeout(() => {
      try { mapInstance.invalidateSize(); } catch (e) { console.warn('Map invalidateSize failed:', e.message); }
    }, 300);
  }
}, [props.visibleModalActivity, mapInstance]);

const handleMapReady = (map) => {
  setMapInstance(map.target);
  setTimeout(() => {
    try { map.target.invalidateSize(); } catch (e) { console.warn('Map invalidateSize failed:', e.message); }
  }, 300);
};

useEffect(() => {
    if (!props.visibleModalActivity) {
      form.resetFields();
      setAttendees([]);
      setValueLat(undefined);
      setValueLng(undefined);
      setAttachmentActivityId([]);
      setAttachmentFileList([]);
      setShowHazardOther(false);
      return;
    }

    const formValues = {
      asset: {
        type: activityActive || undefined,
        date_time: moment(),
      },
    };

    if (editingPolicy) {
      const metadata = parseMetadata(editingPolicy.metadata);

      // Check if this is from hazards table (has workflow_state) or activity_policy table (has metadata)
      const isFromHazardsTable = !!editingPolicy.workflow_state || editingPolicy.current_escalation_level !== undefined;

      const typeValue = editingPolicy.type || metadata.type || activityActive || undefined;
      const dateTimeSource = metadata.date_time || editingPolicy.date_time || editingPolicy.created_at;

      formValues.asset = {
        ...formValues.asset,
        type: typeValue,
        date_time: dateTimeSource ? moment(dateTimeSource) : moment(),
        asset_id: metadata.asset_id || editingPolicy.asset_id,
        // Use direct properties for hazards table, fallback to metadata for activity_policy
        hazard_category: editingPolicy.hazard_category || metadata.hazard_category,
        hazard_type: editingPolicy.hazard_type || metadata.hazard_type,
        hazard_type_other: editingPolicy.hazard_type_other || metadata.hazard_type_other,
        // Biosecurity taxonomy — only ever set on hazards in the Biosecurity
        // category. Read straight off the hazard row (no metadata fallback: these
        // columns are new, so no activity_policy ever carried them).
        report_type: editingPolicy.report_type,
        event_type: editingPolicy.event_type,
        event_status: editingPolicy.event_status,
        domain: editingPolicy.domain,
        actions: editingPolicy.description || metadata.actions,
        comments: metadata.comments,
        notes: metadata.notes,
        hazard_status: editingPolicy.status || metadata.hazard_status,
        actions_to_rectify: editingPolicy.actions_to_rectify || metadata.actions_to_rectify,
        allocated_to: editingPolicy.allocated_to || metadata.allocated_to,
        rectification_due_date: editingPolicy.rectification_due_date
          ? moment(editingPolicy.rectification_due_date)
          : (metadata.rectification_due_date ? moment(metadata.rectification_due_date) : undefined),
        is_closed: editingPolicy.is_closed || metadata.is_closed || metadata.hazard_closed || false,
        current_assignee_id: editingPolicy.workflow_state?.current_assignee_id,
        deadline: editingPolicy.deadline ? moment.utc(editingPolicy.deadline).local() : undefined,
        hazard_radius: editingPolicy.hazard_radius || metadata.hazard_radius || 10,
        reporter: metadata.reporter || undefined,
      };

      const attendeeValues = Array.isArray(metadata.attendees)
        ? metadata.attendees
        : metadata.attendees
        ? [metadata.attendees]
        : [];
      setAttendees(attendeeValues);

      setAttachmentActivityId(extractAttachmentIds(editingPolicy));
      setAttachmentFileList(normalizeAttachments(extractAttachmentSources(editingPolicy)));

      // Handle location from either source. New-table hazards expose lat/lng
      // at the top level rather than under a nested point, so fall through to
      // those if neither source provides a point object.
      const topLevelPoint =
        editingPolicy.lat !== undefined && editingPolicy.lat !== null &&
        editingPolicy.lng !== undefined && editingPolicy.lng !== null
          ? { lat: editingPolicy.lat, lng: editingPolicy.lng }
          : null;
      const point = editingPolicy.point || metadata.point || topLevelPoint;
      if (point) {
        const lat = normalizeCoordinate(point.lat ?? point.latitude);
        const lng = normalizeCoordinate(point.lng ?? point.longitude);
        setValueLat(lat);
        setValueLng(lng);
        if (lat !== null && lng !== null) {
          setPosition([lat, lng]);
        } else {
          setPosition([null, null]);
        }
      } else {
        setValueLat(undefined);
        setValueLng(undefined);
        setPosition([null, null]);
      }

      setShowHazardOther((editingPolicy.hazard_type || metadata.hazard_type) === 'other');
    } else {
      setAttendees([]);
      setAttachmentActivityId([]);
      setAttachmentFileList([]);
      if (initialPoint && initialPoint.lat !== undefined && initialPoint.lng !== undefined) {
        // Standard marker point with lat/lng
        const lat = normalizeCoordinate(initialPoint.lat);
        const lng = normalizeCoordinate(initialPoint.lng);
        setValueLat(lat);
        setValueLng(lng);
        if (lat !== null && lng !== null) {
          setPosition([lat, lng]);
        } else {
          setPosition([null, null]);
        }
      } else if (initialPoint && initialPoint.shape_type && initialPoint.shape_type !== 'marker') {
        // Polygon/rectangle/circle - no direct lat/lng, position will be computed from geometry
        setValueLat(undefined);
        setValueLng(undefined);
        setPosition([null, null]);
      } else {
        setValueLat(undefined);
        setValueLng(undefined);
        setPosition([null, null]);
      }
      setShowHazardOther(false);
    }

    form.setFieldsValue(formValues);

    // Restore pending form data if navigated from Draw Hazard Area flow
    // Data expires after 10 minutes to prevent stale form state from lingering
    const PENDING_DATA_TTL_MS = 10 * 60 * 1000;
    let storedPendingData;
    try {
      storedPendingData = localStorage.getItem(`pendingHazardFormData_${propertyId}`);
    } catch (e) {
      storedPendingData = null;
    }
    // Any existing restored id belongs to a previous modal session; clear it
    // before we decide whether this session should restore a new one.
    setRestoredEditingPolicyId(null);
    if (storedPendingData && !editingPolicy) {
      try {
        localStorage.removeItem(`pendingHazardFormData_${propertyId}`);
        const pendingData = JSON.parse(storedPendingData);
        if (pendingData.savedAt && (Date.now() - pendingData.savedAt) > PENDING_DATA_TTL_MS) {
          // Data is stale — discard it
          throw new Error('Pending hazard form data expired');
        }
        if (pendingData.formValues?.asset) {
          const restored = { ...pendingData.formValues.asset };
          // Convert ISO date strings back to moment objects
          if (restored.date_time) {
            restored.date_time = moment(restored.date_time);
          }
          if (restored.rectification_due_date) {
            restored.rectification_due_date = moment(restored.rectification_due_date);
          }
          if (restored.deadline) {
            restored.deadline = moment(restored.deadline);
          }
          form.setFieldsValue({ asset: restored });
        }
        if (pendingData.attendees) setAttendees(pendingData.attendees);
        if (pendingData.attachmentActivityId) setAttachmentActivityId(pendingData.attachmentActivityId);
        if (pendingData.editingPolicyId) setRestoredEditingPolicyId(pendingData.editingPolicyId);
      } catch (e) {
        console.error('Error restoring pending hazard form data:', e);
      }
    }
  }, [props.visibleModalActivity, editingPolicy, activityActive, form, initialPoint]);

  useEffect(() => {
    if (!propertyId || !props.visibleModalActivity) {
      return;
    }

    propertyService.getListUsersOfProperty(propertyId, true)
      .then((res) => {
        const users = Array.isArray(res?.users)
          ? res.users
          : Array.isArray(res?.data?.users)
            ? res.data.users
            : [];

        // Store full user objects for workflow fields
        setUserOptions(users.filter(user => user && (user.name || user.email)));
      })
      .catch((error) => {
        console.error('Failed to load users for property', error);
      });
  }, [propertyId, props.visibleModalActivity]);


  useEffect(() => {
    if (initialPoint && initialPoint.lat !== undefined && initialPoint.lng !== undefined) {
      const lat = normalizeCoordinate(initialPoint.lat);
      const lng = normalizeCoordinate(initialPoint.lng);
      if (lat !== null && lng !== null) {
        setPosition([lat, lng]);
        return;
      }
    }

    if (editingPolicy) {
      return;
    }

    if (propertyCenter && propertyCenter.lat !== undefined && propertyCenter.lng !== undefined) {
      const lat = normalizeCoordinate(propertyCenter.lat);
      const lng = normalizeCoordinate(propertyCenter.lng);
      if (lat !== null && lng !== null) {
        setPosition([lat, lng]);
        return;
      }
    }

    setPosition([null, null]);
  }, [propertyCenter, initialPoint, editingPolicy]);

  const handleUploadChange = useCallback(({ file, fileList }) => {
    const normalizedList = fileList.map((item) => {
      if (item.status === 'done' && item.response?.attachment) {
        const attachmentData = item.response.attachment;
        return {
          uid: attachmentData.id?.toString() || item.uid,
          name: getAttachmentName(attachmentData),
          status: 'done',
          url: buildAttachmentUrl(attachmentData),
          response: item.response,
        };
      }
      return item;
    });

    setAttachmentFileList(normalizedList);

    if (file.status === 'done' && file.response?.attachment?.id) {
      addAttachmentId(file.response.attachment.id);
    }
  }, [addAttachmentId]);

  const handleUploadRemove = useCallback((file) => {
    const identifier = file.response?.attachment?.id ?? file.uid;
    removeAttachmentId(identifier);
    setAttachmentFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    if (identifier) {
      attachment.destroy(identifier).catch(() => {});
    }
  }, [removeAttachmentId]);

  const handleChangeTag = (input_tags) => {
    setAttendees(input_tags);
  }

  const handleClickMap = (e) => {
     const { lat, lng } = e.latlng;
     setValueLng(lng);
     setValueLat(lat);
     setPosition([lat, lng]);
  };

  const handleDrawHazardArea = () => {
    // Save current form data so it persists across navigation
    try {
      const currentValues = form.getFieldsValue();
      if (currentValues?.asset) {
        const toSave = { ...currentValues.asset };
        // Serialize moment objects to ISO strings
        if (toSave.date_time && moment.isMoment(toSave.date_time)) {
          toSave.date_time = toSave.date_time.toISOString();
        }
        if (toSave.rectification_due_date && moment.isMoment(toSave.rectification_due_date)) {
          toSave.rectification_due_date = toSave.rectification_due_date.toISOString();
        }
        if (toSave.deadline && moment.isMoment(toSave.deadline)) {
          toSave.deadline = toSave.deadline.toISOString();
        }
        localStorage.setItem(`pendingHazardFormData_${propertyId}`, JSON.stringify({
          formValues: { asset: toSave },
          attendees,
          attachmentActivityId,
          editingPolicyId: editingPolicy?.id ?? restoredEditingPolicyId ?? null,
          savedAt: Date.now(),
        }));
      }
    } catch (e) {
      console.error('Error saving pending hazard form data:', e);
    }

    if (typeof props.onRequestDrawArea === 'function') {
      // On PropertyMap — close modal and re-open shape selector
      props.onRequestDrawArea();
    } else {
      // On HealthSafety page — close modal and navigate to map
      props.cancelModalActivity();
      history.push('/map');
      dispatch(changeCurrent(['map']));
      // Use sessionStorage flag — PropertyMap checks this on mount
      sessionStorage.setItem('pendingDrawArea', 'true');
    }
  };

  const handleOnUploadSuccess = (attachment) => {
    setAttachmentActivityId(attachmentActivityId => [...attachmentActivityId, attachment.id]);
  }

  const handleOnRemove = (file) => {
    attachment.destroy(file.uid);
  }
  const mapCenter = useMemo(() => {
    const normalizedLat = normalizeCoordinate(valueLat);
    const normalizedLng = normalizeCoordinate(valueLng);
    if (normalizedLat !== null && normalizedLng !== null) {
      return [normalizedLat, normalizedLng];
    }
    if (position && position[0] != null && position[1] != null) {
      return position;
    }

    // Center on hazard shape geometry (polygon centroid or circle center)
    if (initialPoint && initialPoint.shape_type && initialPoint.shape_type !== 'marker') {
      const centroid = getHazardCentroidArray(initialPoint);
      if (centroid) return centroid;
    }

    // Use primary object centroid if available
    if (primaryObjects && primaryObjects.length > 0 && primaryObjects[0]) {
      const firstObject = primaryObjects[0];
      if (firstObject.lat_centroid && firstObject.lng_centroid) {
        return [
          normalizeCoordinate(firstObject.lat_centroid),
          normalizeCoordinate(firstObject.lng_centroid)
        ];
      }
    }

    const centerLat = normalizeCoordinate(propertyCenter?.lat);
    const centerLng = normalizeCoordinate(propertyCenter?.lng);
    if (centerLat !== null && centerLat !== undefined && centerLng !== null && centerLng !== undefined) {
      return [centerLat, centerLng];
    }
    return [DEFAULT_LAT, DEFAULT_LNG];  // ← Changed from null to default coords
  }, [valueLat, valueLng, position, propertyCenter, primaryObjects, initialPoint]);

  const uploadPolicyProps = {
    name: "Attachment[file]",
    action: apiEndpoint.attachments,
    headers: authHeader(true),
    listType: "picture",
    multiple: true,
    fileList: attachmentFileList,
    onChange: handleUploadChange,
    onRemove: handleUploadRemove,
    data: saveToHazardsTable ? {
      'Attachment[object_type]': 'hazard',
      'Attachment[object_id]': editingPolicy?.id || null
    } : undefined,
  };

  const getLatLng = (cLat, cLng) => new L.LatLng(cLat, cLng);
  const renderActivityPolicy = () => {
    let options = [];
    _.forEach(activityPolicy , (activity, index) => {
      options.push(
        <Option key={index} value={activity.value}>
          <div>{activity.label}</div>
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select an activity"
        allowClear
        size="large"
        disabled ={!activityActive ?false:true}
      
      >
        {options}
      </Select>
    );
  }  
  useEffect(() => {
    if (!hazardCategoryValue) {
      form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
      return;
    }

    const currentHazardType = form.getFieldValue(['asset', 'hazard_type']);
    if (!currentHazardType) {
      return;
    }

    const derivedCategory = getSafetyActivityHazardCategoryByHazard(currentHazardType);
    if (derivedCategory && derivedCategory !== hazardCategoryValue) {
      form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
    }
  }, [hazardCategoryValue, form]);

  useEffect(() => {
    if (!hazardTypeValue) {
      return;
    }

    const derivedCategory = getSafetyActivityHazardCategoryByHazard(hazardTypeValue);
    if (derivedCategory && derivedCategory !== hazardCategoryValue) {
      form.setFieldsValue({
        asset: {
          hazard_category: derivedCategory,
        },
      });
    }
  }, [hazardTypeValue, hazardCategoryValue, form]);

  useEffect(() => {
    if (hazardTypeValue === 'other') {
      setShowHazardOther(true);
    } else {
      if (showHazardOther) {
        form.resetFields([['asset', 'hazard_type_other']]);
      }
      setShowHazardOther(false);
    }
  }, [hazardTypeValue, form, showHazardOther]);

  useEffect(() => {
    if (!props.visibleModalActivity) {
      setShowHazardOther(false);
    }
  }, [props.visibleModalActivity]);

  // Fetch primary objects (polygons) when modal opens
  useEffect(() => {
    if (props.visibleModalActivity && propertyId) {
      propertyService.viewProperty(propertyId).then(res => {
        if (res.code === '0000' && res.primary_objects) {
          setPrimaryObjects(res.primary_objects);
        }
      }).catch(err => {
        console.error('Error loading primary objects:', err);
      });
    }
  }, [props.visibleModalActivity, propertyId]);

  // Render primary objects (polygons) on the map
  const renderPrimaryObjects = () => {
    if (!primaryObjects || primaryObjects.length === 0) {
      return null;
    }

    return primaryObjects.map((object) => {
      // Define color mapping similar to PropertyMap
      const colorMap = {
        'blue': '#31a6c5',
        'green': '#52c41a',
        'red': '#f5222d',
        'yellow': '#faad14',
        'purple': '#722ed1',
        'orange': '#fa8c16',
      };

      const color = colorMap[object.color] || '#31a6c5'; // Default to blue

      return (
        <Polygon
          key={object.id}
          positions={object.area}
          fillColor={color}
          color={color}
          fillOpacity={0.2}
          weight={2}
        >
          <Tooltip permanent={true} direction="center" className="primary-objects-tooltip">
            <div>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{object.name || 'Unnamed Area'}</p>
            </div>
          </Tooltip>
        </Polygon>
      );
    });
  };

  // Render a preview of the hazard shape being created in the mini-map
  const renderHazardShapePreview = () => {
    if (!effectiveGeometry) {
      return null;
    }

    if (effectiveGeometry.shape_type === 'polygon' || effectiveGeometry.shape_type === 'rectangle') {
      const positions = effectiveGeometry.data_area
        .map(coord => {
          const parts = coord.split(',').map(v => parseFloat(v.trim()));
          if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
          return [parts[0], parts[1]];
        })
        .filter(Boolean);
      if (positions.length === 0) return null;
      return (
        <Polygon
          positions={positions}
          color="#ff4d4f"
          fillColor="#ff4d4f"
          fillOpacity={0.3}
          weight={2}
        />
      );
    }

    if (effectiveGeometry.shape_type === 'circle') {
      return (
        <Circle
          center={[effectiveGeometry.circle_center.lat, effectiveGeometry.circle_center.lng]}
          radius={parseFloat(effectiveGeometry.circle_radius) || 10}
          color="#ff4d4f"
          fillColor="#ff4d4f"
          fillOpacity={0.3}
          weight={2}
        />
      );
    }

    return null;
  };

  const handleHazardCategoryChange = () => {
    form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
  };

  const handleHazardSpecificChange = (value) => {
    if (value !== 'other') {
      form.resetFields([['asset', 'hazard_type_other']]);
    }
  };
  const handleSubmit = (e) => {
    setLoading(true);
    
    form
      .validateFields()
      .then(values => {
        onStore(values);
       

      })
      .catch(info => {
        console.log('Validate Failed:', info);
        setLoading(false);
      });
  }
  const onStore = (values) => {
    var point = null;
    const lat = normalizeCoordinate(valueLat);
    const lng = normalizeCoordinate(valueLng);
    if(lat !== null && lng !== null){
      point = getLatLng(lat,lng);
    }

    const policyId = editingPolicy?.id ?? restoredEditingPolicyId ?? null;

    // NEW: Save to hazards table if saveToHazardsTable prop is true
    if (saveToHazardsTable) {
      // Health-safety statuses are directly supported in hazards table
      // rectified, not_rectified, made_safe_in_progress, made_safe_no_action pass through as-is
      const isClosed = values.asset.is_closed || false;
      const hazardStatus = values.asset.hazard_status || 'open';

      // Determine geometry from the resolved source (a freshly-drawn shape or,
      // when editing, the saved hazard's geometry). Using effectiveGeometry here
      // is what prevents an edit-save from silently downgrading a polygon/circle
      // hazard to a marker.
      const geometryData = {};
      if (effectiveGeometry) {
        geometryData.shape_type = effectiveGeometry.shape_type;
        if (effectiveGeometry.data_area) {
          geometryData.data_area = effectiveGeometry.data_area;
        }
        if (effectiveGeometry.circle_center) {
          geometryData.circle_center = effectiveGeometry.circle_center;
          geometryData.circle_radius = effectiveGeometry.circle_radius;
        }
      } else {
        geometryData.shape_type = 'marker';
      }

      // Marker-shape hazards must have lat/lng. If the user has cleared the
      // coordinate inputs (or never set them) we block the save here instead
      // of letting the backend preserve the old location silently — the user
      // can close the modal to revert, or enter new coordinates to continue.
      if (geometryData.shape_type === 'marker' && (lat === null || lng === null)) {
        message.error('Latitude and Longitude are required. Click the map or enter coordinates to place the hazard.');
        setLoading(false);
        return;
      }

      const hazardData = {
        hazard: {
          title: values.asset.hazard_type || values.asset.hazard_type_other || 'Hazard Report',
          // Form loads `actions` from `editingPolicy.description`; keep the save
          // symmetric so the details textarea round-trips. The old `||
          // values.asset.comments` fallback caused typing in the comments box
          // to overwrite details on reopen when actions still held the old
          // value. The comments field is no longer rendered for hazards (see
          // Form.Item condition below).
          description: values.asset.actions || '',
          hazard_type: values.asset.hazard_type || values.asset.hazard_type_other,
          hazard_category: values.asset.hazard_category,
          // Biosecurity classification. Sent only for the Biosecurity category, and
          // explicitly nulled otherwise so switching a hazard out of Biosecurity
          // does not leave stale classification behind.
          ...(isBiosecurityHazardCategory(values.asset.hazard_category)
            ? {
              report_type: values.asset.report_type || null,
              event_type: values.asset.event_type || null,
              event_status: values.asset.event_status || null,
              domain: values.asset.domain || null,
            }
            : { report_type: null, event_type: null, event_status: null, domain: null }),
          severity: determineSeverity(values.asset.hazard_category),
          status: hazardStatus,
          is_closed: isClosed,
          current_escalation_level: isClosed ? 0 : 1,
          property_id: propertyId,
          point: point,
          // Polygon/circle geometry fields
          ...geometryData,
          attachment_ids: attachmentActivityId && attachmentActivityId.length > 0 ? attachmentActivityId : [],
          // When the hazard was FOUND. The modal has always required this and
          // then dropped it on the floor — it was never in this payload, so a
          // hazard found at dawn and filed at smoko looked like it appeared at
          // smoko. Now stored on hazards.occurred_at.
          date_time: values.asset.date_time ? dateHelper.dateTimeForAPI(values.asset.date_time) : null,
          allocated_to: values.asset.allocated_to || null,
          actions_to_rectify: values.asset.actions_to_rectify || null,
          rectification_due_date: values.asset.rectification_due_date ? dateHelper.dateForAPI(values.asset.rectification_due_date) : null,
          // Workflow fields (for editing existing hazards)
          current_assignee_id: values.asset.current_assignee_id || null,
          deadline: values.asset.deadline ? dateHelper.dateTimeForAPI(values.asset.deadline) : null,
          hazard_radius: values.asset.hazard_radius || 10,
        }
      };

      hazardService.storeOrUpdateHazard(hazardData, policyId).then(res => {
        if(res && res.code === '0000'){
          setLoading(false);
          message.success(policyId ? 'Hazard updated successfully' : 'Hazard saved successfully');
          form.resetFields();
          props.cancelModalActivity();
          props.searchAssetAndPolicy();
          window.dispatchEvent(
            new CustomEvent('healthSafety:activityRefresh', {
              detail: { hazard: res.hazard ?? null },
            })
          );
          setAttendees([]);
          setValueLat();
          setValueLng();
          setAttachmentActivityId([]);
          setAttachmentFileList([]);
          setRestoredEditingPolicyId(null);
        } else {
          setLoading(false);
          message.error(res?.message || 'Failed to save hazard');
        }
      }).catch(err => {
        setLoading(false);
        message.error('Failed to save hazard');
      });
      return;
    }

    // EXISTING: Save to activity_policy table (for Health-Safety activities)
    const dataPolicy = {
      policy: {
        ...values.asset,
        property_id: propertyId ?? null,
        type: values.asset.type,
        date_time: values.asset.date_time ? dateHelper.dateTimeForAPI(values.asset.date_time) : '',
        point:point,
        attendees: attendees,
        attachment_activity_id:attachmentActivityId,
        rectification_due_date: values.asset.rectification_due_date ? dateHelper.dateForAPI(values.asset.rectification_due_date) : null,
      },
    };

    healthsafety.storeOrUpdateActivityPolicies(dataPolicy, policyId).then(res => {
      if(res.code === '0000'){
        setLoading(false);
        form.resetFields();
        props.cancelModalActivity();
        props.searchAssetAndPolicy();
        window.dispatchEvent(
          new CustomEvent('healthSafety:activityRefresh', {
            detail: { policy: res.policy ?? res.activity ?? null },
          })
        );
        setAttendees([]);
        setValueLat();
        setValueLng();
        setAttachmentActivityId([]);
        setAttachmentFileList([]);
        if(currentUser){
           const notiData = {
            "type": "Activity",
            "data":{
              "title": "New Activity",
              "user_id_send": currentUser.id,
              "user_name_send":currentUser.name,
              "body" : "added Activity "+values.asset.type.replace(/_/g, ' ').replace(/(?: |\b)(\w)/g, l => l.toUpperCase()),
            }
          }
          message.success('Form added successfully!');
          dispatch(notificationData(notiData));
        }else{
          console.log('Error User');
        }
       

      } else {
        setLoading(false);
        message.error(res?.message || 'Failed to save');
      }
    }).catch(err => {
      setLoading(false);
      message.error('Failed to save');
    });
  }

  // Helper function for hazard data mapping
  const determineSeverity = (category) => {
    // Default to 'medium', can be enhanced with more sophisticated logic later
    return 'medium';
  };

  return (
    <>
      <ConfirmModalStyles />
      <ModalActivityWrap
        visible={props.visibleModalActivity}
      title={activityActive === 'safety_walk' ? "Safety Walk" : activityActive === "hazard" ? "Hazard" : <IntlMessages id="propertyPage.modal.safetyActivity.title" />}
      onCancel={() => {
        Modal.confirm({
          title: 'Are you sure?',
          content: 'Any unsaved changes will be lost. Do you want to proceed?',
          okText: 'Yes, close',
          cancelText: 'No, stay',
          onOk: () => {
            props.cancelModalActivity();
          }
        });
      }}
      maskClosable={false}
      closable={true}
      footer={[
      <Button key="back" onClick={props.cancelModalActivity} className="cancel-button">
        {<IntlMessages id="propertyPage.modal.cancel" />}
      </Button>,
      <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
        {<IntlMessages id="propertyPage.modal.save" />}
      </Button>]}
    >
     <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        id="#1"
      >
      <div className="form-body">
      
      <Row>
        <Col xs={{span: 24}} md={{span: 24}}>
            <Form.Item
              name={['asset','type']}
              className="isoInputWrapper"
              label={<IntlMessages id="propertyPage.modal.asset.activityPolicy" />}
              style={{display: shouldHideActivityPolicy ? 'none' : 'block'}}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              {renderActivityPolicy()}
            </Form.Item>
          </Col>
      </Row>
     
      <Row>
          <Col xs={{span: 24}} md={{span: 24}}>
            <Form.Item
              name={['asset', 'date_time']}
              className="isoInputWrapper"
              label={<IntlMessages id="healthSafety.history.dateTime" />}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              <DatePicker format="DD-MM-YYYY HH:mm:ss" size="large" style={{width: '100%'}} showTime/>
            </Form.Item>
          </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Form.Item
            className="isoInputWrapper"
            label={<IntlMessages id="healthSafety.history.capturedBy" />}
          >
            <Input value={capturedByValue} disabled />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Form.Item
            className="isoInputWrapper"
            label={<IntlMessages id="healthSafety.history.capturedAt" />}
          >
            <Input value={capturedTimestamp} disabled />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            return ( prevValues['asset'] );
          }}
        >
        {({ getFieldValue }) => {
          if(getFieldValue('asset')){
             
            return (
              <div>
              {(getFieldValue('asset').type == 'hazard' || 
                getFieldValue('asset').type == 'near_miss' || 
                getFieldValue('asset').type == 'accident') && (
                  <>
                    <Row>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          name={['asset','hazard_category']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.hazardCategory" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select a hazard category"
                            allowClear
                            onChange={handleHazardCategoryChange}
                            size="large"
                          >
                            {safetyActivityHazardCategories.map((category) => (
                              <Option key={category.value} value={category.value}>
                                <div>{category.label}</div>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          name={['asset','hazard_type']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.hazardDetail" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Select
                            placeholder={hazardCategoryValue ? "Select a hazard" : "Select a hazard category first"}
                            allowClear
                            onChange={handleHazardSpecificChange}
                            size="large"
                            disabled={!hazardCategoryValue}
                            showSearch
                            optionFilterProp="children"
                          >
                            {hazardOptions.map((hazard) => (
                              <Option key={hazard.value} value={hazard.value}>
                                <div>{hazard.label}</div>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    {/* Biosecurity classification. Four independent axes, shown only
                        for the Biosecurity category — every other hazard category
                        leaves them null. A hazard raised automatically from a
                        Biosecurity Report arrives with these pre-filled from the
                        report; whoever triages it refines them here. */}
                    {isBiosecurityHazardCategory(hazardCategoryValue) && (
                      <Row gutter={16}>
                        {BIOSECURITY_TAXONOMY.map((axis) => (
                          <Col xs={{ span: 24 }} md={{ span: 12 }} key={axis.name}>
                            <Form.Item
                              name={['asset', axis.name]}
                              className="isoInputWrapper"
                              label={axis.label}
                              tooltip={axis.tooltip}
                            >
                              <Select
                                placeholder={`Select ${axis.label.toLowerCase()}`}
                                allowClear
                                size="large"
                                options={axis.options}
                              />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </>
                )}
                {showHazardOther &&
                  <Row>
                    <Col xs={{span: 24}} md={{span: 24}}>
                      <Form.Item
                        name={['asset', 'hazard_type_other']}
                        className="isoInputWrapper"
                        label={<IntlMessages id="healthSafety.history.hazardTypeOther" />}
                        rules={[
                          {
                            required: true,
                            message: 'This field is required',
                          },
                        ]}
                      >
                        <Input size="large" placeholder="Hazard type"/>
                      </Form.Item>
                    </Col>
                  </Row>
                }
                { getFieldValue('asset').type === 'meeting' &&
                  <div>
                    <Row>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          name={['asset', 'meeting_name']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.meetingName" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Meeting Name"/>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                }
                {(getFieldValue('asset').type == 'safety_walk' || 
                  getFieldValue('asset').type == 'hazard' || 
                  getFieldValue('asset').type == 'meeting'||
                  getFieldValue('asset').type == 'induction'||
                  getFieldValue('asset').type === 'toolbox_meeting' || 
                  getFieldValue('asset').type == 'near_miss' || 
                  getFieldValue('asset').type == 'accident'
                  ) &&
                  <Row>
                  <Col xs={{span: 24}} md={{span: 24}}>
                      <Form.Item
                        name={['asset', 'actions']}
                        className="isoInputWrapper"
                        label={<IntlMessages id="healthSafety.history.details" />}
                        rules={[
                          {
                            required: false,
                            message: 'This field is required',
                          },
                        ]}
                      >
                        <Input.TextArea />
                      </Form.Item>
                    </Col>
                  </Row>
                }
                {getFieldValue('asset').type == 'induction' &&
                    <Row>
                      <Col xs={{span: 24}} md={{span: 24}}>
                        <Form.Item
                          name={['asset', 'person_running_induction']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.personRunningInduction" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Person running Induction"/>
                        </Form.Item>
                      </Col>
                    </Row>
                }
                {(
                getFieldValue('asset').type === 'meeting' || 
                getFieldValue('asset').type === 'safety_walk' || 
                getFieldValue('asset').type === 'induction'||
                getFieldValue('asset').type === 'toolbox_meeting'
                ) &&
                <div>
                <Row>
                  <Col xs={{span: 24}} md={{span: 24}}>
                    <Form.Item
                      className="isoInputWrapper"
                      label={<IntlMessages id="healthSafety.history.attendees" />}
                      rules={[
                        {
                          required: true,
                          message: 'This field is required',
                        },
                      ]}
                    >
                      <Select
                        mode="tags"
                        size="large"
                        allowClear
                        tokenSeparators={[',']}
                        placeholder="Select or type attendees"
                        value={attendees}
                        onChange={handleChangeTag}
                        options={userOptionsForSelect}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                </div>
              }
                { (getFieldValue('asset').type === 'comment' ||
                  getFieldValue('asset').type === 'update' ||
                  getFieldValue('asset').type == 'induction' ||
                  getFieldValue('asset').type === 'toolbox_meeting' ||
                  getFieldValue('asset').type == 'near_miss' ||
                  getFieldValue('asset').type == 'accident'
                  ) &&
                    <Row>
                     <Col xs={{span: 24}} md={{span: 24}} >
                      <Form.Item
                        name={['asset', 'comments']}
                        className="isoInputWrapper"
                        label={<IntlMessages id="healthSafety.history.comment" />}
                      >
                        <Input.TextArea />
                      </Form.Item>
                      </Col>
                    </Row>

                  }
                  {getFieldValue('asset').type == 'hazard' &&
                    <div>
                      <Row>
                        <Col xs={{span: 24}} md={{span: 24}}>
                          <Form.Item
                            name={['asset','hazard_status']}
                            className="isoInputWrapper"
                            label="Current Hazard Status"
                          >
                            <Select
                              placeholder="Select hazard status"
                              allowClear
                              size="large"
                            >
                              <Option value="rectified">Rectified</Option>
                              <Option value="not_rectified">Not Rectified</Option>
                              <Option value="made_safe_in_progress">Made Safe, in progress</Option>
                              <Option value="made_safe_no_action">Made safe, no action</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      {getFieldValue(['asset', 'hazard_status']) &&
                        <div>
                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <Form.Item
                                name={['asset', 'actions_to_rectify']}
                                className="isoInputWrapper"
                                label="Actions to Rectify Hazard"
                              >
                                <Input.TextArea rows={4} placeholder="Describe actions needed to rectify this hazard" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'allocated_to']}
                                className="isoInputWrapper"
                                label="Allocate Rectification To"
                              >
                                <Select
                                  placeholder="Select user"
                                  allowClear
                                  size="large"
                                  showSearch
                                  optionFilterProp="children"
                                >
                                  {userOptions.map((user) => {
                                    const displayName = user?.name || user?.email;
                                    const value = user?.name && user?.email && user.name !== user.email
                                      ? `${user.name} (${user.email})`
                                      : displayName;
                                    return (
                                      <Option key={user.id || value} value={value}>
                                        {value}
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'rectification_due_date']}
                                className="isoInputWrapper"
                                label="To be Completed By"
                              >
                                <DatePicker 
                                  format="DD-MM-YYYY" 
                                  size="large" 
                                  style={{width: '100%'}}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      }
                      <Row>
                        <Col xs={{span: 24}} md={{span: 24}}>
                          <Form.Item
                            name={['asset', 'is_closed']}
                            className="isoInputWrapper"
                            valuePropName="checked"
                            tooltip="When checked, hazard will not be escalated and will be marked as resolved"
                          >
                            <Checkbox
                              disabled={hazardStatusValue !== 'rectified'}
                            >
                              Hazard Closed
                              {hazardStatusValue !== 'rectified' &&
                                <span style={{marginLeft: '8px', color: '#999', fontSize: '12px'}}>
                                  (Only available when status is "Rectified")
                                </span>
                              }
                            </Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                      {editingPolicy && editingPolicy.id && (
                        <div>
                          <Row>
                            <Col xs={{span: 24}} md={{span: 24}}>
                              <div style={{backgroundColor: '#e6f7ff', padding: '10px', marginBottom: '16px', borderRadius: '4px'}}>
                                <strong>Current Escalation Level:</strong> L{editingPolicy.current_escalation_level || 1}
                              </div>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'current_assignee_id']}
                                className="isoInputWrapper"
                                label="Current Assignee (Workflow)"
                                tooltip="This is the person responsible in the escalation workflow"
                              >
                                <Select
                                  placeholder="Select assignee"
                                  allowClear
                                  size="large"
                                  showSearch
                                  optionFilterProp="children"
                                >
                                  {userOptions.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                      {user.name} ({user.email})
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{span: 24}} md={{span: 12}}>
                              <Form.Item
                                name={['asset', 'deadline']}
                                className="isoInputWrapper"
                                label="Workflow Deadline"
                                tooltip="Deadline for resolution at current escalation level"
                              >
                                <DatePicker
                                  format="DD-MM-YYYY HH:mm"
                                  size="large"
                                  style={{width: '100%'}}
                                  showTime
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  }
                  {(getFieldValue('asset').type === 'comment' || 
                    getFieldValue('asset').type === 'update' ||
                    getFieldValue('asset').type == 'safety_walk'||
                    getFieldValue('asset').type === 'toolbox_meeting'||
                    getFieldValue('asset').type === 'hazard'
                  ) &&
                  
                      <Row>
                        <Col xs={{span: 24}} md={{span: 24}} >
                          <Form.Item
                            name={['asset', 'attachment']}
                            className="isoInputWrapper"
                            label={<IntlMessages id="propertyPage.modal.asset.attachment" />}
                          >
                            <FileUploader
                              action={apiEndpoint.attachments}
                              name="Attachment[file]"
                              isCompressFile={true}
                              handleOnSuccess={handleOnUploadSuccess}
                              handleOnRemove={handleOnRemove}
                              multiple={true}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    }
                    {getFieldValue('asset').type == 'safety_walk' &&
                      <Row>
                        <Col xs={{span: 24}} md={{span: 24}}>
                          <Form.Item
                            name={['asset', 'reporter']}
                            className="isoInputWrapper"
                            label={<IntlMessages id="healthSafety.history.reporter" />}
                          >
                            <Select
                              size="large"
                              allowClear
                              showSearch
                              placeholder="Select reporter"
                              optionFilterProp="label"
                              options={userOptionsForSelect}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    }
                    {(getFieldValue('asset').type == 'safety_walk' ||  getFieldValue('asset').type === 'meeting' ) &&
                      <Row>
                      <Col xs={{span: 24}} md={{span: 24}} >
                            <Form.Item
                              name={['asset', 'notes']}
                              className="isoInputWrapper"
                              label={<IntlMessages id="healthSafety.history.notes" />}
                            >
                              <Input.TextArea rows={6} placeholder="Enter notes" />
                            </Form.Item>
                          </Col>
                      </Row>
                    }


                  {(getFieldValue('asset').type == 'comment' ||
                    getFieldValue('asset').type == 'hazard' ||
                    getFieldValue('asset').type == 'near_miss' ||
                    getFieldValue('asset').type == 'accident') &&
                    <div>
                      {/* Area Mode Indicator — shown when hazard area geometry exists */}
                      {hasHazardArea && getFieldValue('asset').type === 'hazard' && (
                        <div className="hazard-area-indicator">
                          <span>
                            <EnvironmentOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                            <strong>Hazard Area:</strong> {hazardAreaDescription}
                          </span>
                          <Button size="small" icon={<EditOutlined />} onClick={handleDrawHazardArea}>
                            Edit Area
                          </Button>
                        </div>
                      )}

                      {/* Map Preview */}
                      <Map
                        ref={mapRef}
                        center={mapCenter}
                        doubleClickZoom={false}
                        onClick={hasHazardArea ? undefined : handleClickMap}
                        whenReady={handleMapReady}
                        zoom={16}
                        zoomControl={true}
                        style={{width:'100%', height:'300px'}}
                      >
                        <TileLayer
                          attribution='&copy; Google Maps'
                          url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                        />
                        {/* Render property polygon overlays */}
                        {renderPrimaryObjects()}
                        {/* Render hazard shape preview (polygon/rectangle/circle) */}
                        {renderHazardShapePreview()}
                        {/* Only show pin marker when NOT in area mode */}
                        {!hasHazardArea && (valueLat !== null && valueLat !== undefined && valueLng !== null && valueLng !== undefined) && (
                          <Marker position={[valueLat, valueLng]} draggable={false}>
                            <Popup position={[valueLat, valueLng]}>
                            </Popup>
                          </Marker>
                        )}
                      </Map>

                      {/* "Place Hazard" button — shown directly under map for hazard type when no area yet */}
                      {!hasHazardArea && getFieldValue('asset').type === 'hazard' && (
                        <Row style={{ marginTop: 6 }}>
                          <Col span={24}>
                            <Button
                              block
                              icon={<EnvironmentOutlined />}
                              onClick={handleDrawHazardArea}
                              style={{
                                backgroundColor: '#0B2A1B',
                                borderColor: '#0B2A1B',
                                color: '#ffffff',
                              }}
                            >
                              Place Hazard
                            </Button>
                          </Col>
                        </Row>
                      )}

                      {/* Pin-mode controls — hidden when area is drawn */}
                      {!hasHazardArea && (
                        <>
                          <Row style={{marginTop:"10px", marginBottom:"5px"}}>
                            <Col xs={{span: 24}} md={{span: 11}} >
                              <InputNumber
                                value={valueLat}
                                onChange={setValueLat}
                                style={{width: '100%'}}
                                placeholder="Latitude"
                              />
                            </Col>
                            <Col xs={{span: 24}} md={{span: 11, offset: 2}} >
                              <InputNumber
                                value={valueLng}
                                onChange={setValueLng}
                                style={{width: '100%'}}
                                placeholder="Longitude"
                              />
                            </Col>
                          </Row>
                          {getFieldValue('asset').type === 'hazard' &&
                            <Row style={{marginTop:"5px"}}>
                              <Col xs={{span: 24}} md={{span: 11}}>
                                <Form.Item
                                  name={['asset', 'hazard_radius']}
                                  className="isoInputWrapper"
                                  label="Hazard Zone Radius (meters)"
                                >
                                  <InputNumber
                                    min={1}
                                    max={10000}
                                    size="large"
                                    style={{ width: '100%' }}
                                    placeholder="Default: 10 meters"
                                    disabled={valueLat === null || valueLat === undefined || valueLng === null || valueLng === undefined}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          }
                        </>
                      )}
                    </div>
                  }
              </div>
            )
          }
        }}

      </Form.Item>
        
      </div>
      </Form>

    </ModalActivityWrap>
    </>
  );
}

export default ModalActivity;
